import React, { useEffect, useRef } from 'react';
import { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import { AnimatedNumber } from '@/components/AnimatedNumber';
import { TextProps } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { appTheme } from '@/components/lib/theme';
import { OddsFormat } from '@/hooks/use-auth-user-settings';
import { useOddsFormat } from '@/hooks/use-odds-format';
import { formatOdds } from '@/utils/format-odds';

import { useIsOddsBetrBucks } from '../hooks/betslip-hooks';
import { AnimatedOddsChangeIcon } from './AnimatedOddsChangeIcon';

const ANIMATION_DURATION = 500;

type BetSlipOddsProps = {
    betId: string;
    odds: number;
    previousOdds: number | null;
    hasOddsChanged: boolean;
} & Omit<TextProps, 'ref'>;

const getOddsChangeColor = (odds: number, previousOdds: number | null): string | null => {
    if (!previousOdds) {
        return null;
    }
    if (odds > previousOdds) {
        return ds.colorValue.text.success;
    }
    if (odds < previousOdds) {
        return ds.colorValue.text.danger;
    }
    return null;
};

export const BetSlipOdds = ({ betId, odds, previousOdds, hasOddsChanged, ...textProps }: BetSlipOddsProps) => {
    const isBetrBucks = useIsOddsBetrBucks(betId);
    const oddsFormat = useOddsFormat();
    const needsColorReset = useRef(false);

    const colorKey = typeof textProps.color === 'string' ? textProps.color : ds.colorToken.text.brand;
    const defaultColor = appTheme.colors[colorKey] ?? ds.colorValue.text.brand;
    const oddsChangeColor = getOddsChangeColor(odds, previousOdds);

    const animatedColor = useSharedValue(defaultColor);

    useEffect(() => {
        if (hasOddsChanged && oddsChangeColor) {
            needsColorReset.current = true;
            animatedColor.value = oddsChangeColor;
        } else if (!hasOddsChanged && needsColorReset.current) {
            animatedColor.value = withTiming(defaultColor, { duration: ANIMATION_DURATION });
            needsColorReset.current = false;
        }
    }, [hasOddsChanged, oddsChangeColor, defaultColor, animatedColor]);

    const animatedTextStyle = useAnimatedStyle(() => ({
        color: animatedColor.value,
    }));

    return (
        <Box flexDirection="row" alignItems="center">
            <Box width={20} height={20} marginRight="s4">
                <AnimatedOddsChangeIcon hasOddsChanged={hasOddsChanged} previousOdds={previousOdds} odds={odds} />
            </Box>
            {oddsFormat === OddsFormat.AMERICAN ? (
                // We don't adjust the display odds for betr bucks as it will create invalid american odds
                <AnimatedNumber
                    value={odds}
                    {...textProps}
                    animatedTextStyle={animatedTextStyle}
                    displayFormat={(value: number) => formatOdds(oddsFormat, value)}
                />
            ) : (
                <AnimatedNumber
                    value={isBetrBucks ? odds - 1 : odds}
                    {...textProps}
                    animatedTextStyle={animatedTextStyle}
                    appendText="x"
                />
            )}
        </Box>
    );
};
